import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X, Expand } from "lucide-react";
import { MaskLines, Overline, Reveal } from "./Motion";
import { GALLERY } from "../../lib/site";
import { lockScroll } from "../../lib/scroll";

const Lightbox = ({ index, setIndex }) => {
  const n = GALLERY.length;
  const next = useCallback(() => setIndex((i) => (i + 1) % n), [n, setIndex]);
  const prev = useCallback(() => setIndex((i) => (i - 1 + n) % n), [n, setIndex]);

  useEffect(() => {
    lockScroll(true);
    const onKey = (e) => {
      if (e.key === "Escape") setIndex(null);
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      lockScroll(false);
    };
  }, [next, prev, setIndex]);

  const item = GALLERY[index];
  return (
    <motion.div data-testid="gallery-lightbox" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] flex items-center justify-center bg-[#05070B]/95 backdrop-blur-md p-4" onClick={() => setIndex(null)}>
      <button aria-label="Close" data-testid="lightbox-close" onClick={() => setIndex(null)}
        className="absolute right-4 top-4 grid h-11 w-11 place-items-center rounded-full border border-white/20 text-white hover:bg-white/10"><X className="h-5 w-5" /></button>
      <button aria-label="Previous" data-testid="lightbox-prev" onClick={(e) => { e.stopPropagation(); prev(); }}
        className="absolute left-3 sm:left-8 z-10 grid h-12 w-12 place-items-center rounded-full border border-white/20 bg-black/40 text-white hover:bg-white/10"><ChevronLeft className="h-5 w-5" /></button>
      <button aria-label="Next" data-testid="lightbox-next" onClick={(e) => { e.stopPropagation(); next(); }}
        className="absolute right-3 sm:right-8 z-10 grid h-12 w-12 place-items-center rounded-full border border-white/20 bg-black/40 text-white hover:bg-white/10"><ChevronRight className="h-5 w-5" /></button>
      <figure className="relative" onClick={(e) => e.stopPropagation()}>
        <motion.img key={item.src} src={item.src} alt={item.cap} initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }} data-testid="lightbox-image"
          className="max-h-[80vh] max-w-[92vw] rounded-2xl object-contain" />
        <figcaption className="mt-4 flex justify-between text-sm text-slate-300">
          <span>{item.cap}</span><span className="font-mono-k text-slate-400">{index + 1} / {n}</span>
        </figcaption>
      </figure>
    </motion.div>
  );
};

export const Gallery = () => {
  const [index, setIndex] = useState(null);
  return (
    <section id="gallery" data-testid="gallery-section" className="relative bg-[#0E131D] py-24 lg:py-36">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Overline>Workspace Gallery</Overline>
        <MaskLines className="font-display mt-6 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.03em] leading-[1.02] text-white"
          lines={["Step inside", <span className="text-gold">before you visit.</span>]} />
        <div className="mt-16 columns-2 md:columns-3 gap-4 sm:gap-5">
          {GALLERY.map((g, i) => (
            <Reveal key={g.src + i} delay={(i % 3) * 0.06} className="mb-4 sm:mb-5 break-inside-avoid">
              <button onClick={() => setIndex(i)} data-testid="gallery-image-thumbnail"
                className={`group relative block w-full overflow-hidden rounded-[20px] border border-white/10 ${g.ratio}`}>
                <img src={g.src} alt={g.cap} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1.2s] group-hover:scale-110" />
                <span className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-80 transition-opacity group-hover:opacity-100" />
                <span className="absolute bottom-3 left-4 right-4 flex items-center justify-between text-left text-sm text-white">
                  {g.cap}<Expand className="h-4 w-4 opacity-0 transition-opacity group-hover:opacity-100" />
                </span>
              </button>
            </Reveal>
          ))}
        </div>
      </div>
      <AnimatePresence>{index !== null && <Lightbox index={index} setIndex={setIndex} />}</AnimatePresence>
    </section>
  );
};
